"use client"

import Link from "next/link"
import { awalKataKapital } from "@/shared/lib/format-nama"
import { DynamicIcon } from "@/shared/components/dynamic-icon"
import { useInfoSitus } from "./info-situs-provider"
import { IndikatorStatus } from "@/modules/status/ui/indikator-status"

export function Footer() {
  const currentYear = new Date().getFullYear()
  const tahunCopyright = currentYear > 2025 ? `2025 - ${currentYear}` : "2025"
  const { info, kontak: kategori, metodeBayar } = useInfoSitus()

  const kontakKategoriPertama = kategori[0]?.items ?? []
  const totalKontak = kategori.reduce((total, k) => total + k.items.length, 0)
  const sisaKontak = totalKontak - kontakKategoriPertama.length

  return (
    <footer className="relative mt-20 bg-[#12151c] text-[#a8b0bf]">
      <div className="h-[3px] bg-gradient-to-r from-primary via-red-400 to-primary" />

      <div className="container mx-auto px-4 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">

          <div className="col-span-1 md:col-span-5 lg:col-span-4">
            <div className="flex items-center gap-2.5 mb-4">
              <img src="/icon/nobg.svg" alt={`Logo ${info.appName}`} width={36} height={36} loading="lazy" decoding="async" className="w-9 h-9 object-contain" />
              <span className="text-2xl font-bold text-white tracking-tight">{info.appName}</span>
            </div>
            <p className="text-sm leading-relaxed mb-6 max-w-sm">
              {info.description}
            </p>

            <div className="flex flex-wrap items-center gap-2.5">
              {kontakKategoriPertama.map((item) => (
                <a
                  key={item.slug}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-[#a8b0bf] hover:bg-primary hover:text-white transition-colors duration-200"
                  title={item.nama}
                  aria-label={item.nama}
                >
                  <DynamicIcon iconType={item.iconType} iconValue={item.iconValue} warna={item.warna} className="w-[18px] h-[18px]" />
                </a>
              ))}

              {sisaKontak > 0 && (
                <Link
                  href="/contact"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-xs font-semibold text-[#a8b0bf] hover:bg-primary hover:text-white transition-colors duration-200"
                  title={`${sisaKontak} kontak lainnya`}
                >
                  +{sisaKontak}
                </Link>
              )}
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-2" />

          <div className="col-span-1 md:col-span-2 lg:col-span-2">
            <h2 className="text-sm font-semibold text-white mb-5">Produk</h2>
            <ul className="space-y-3.5">
              <li><Link href="/" className="text-sm hover:text-white transition-colors">Beranda</Link></li>
              <li><Link href="/feature" className="text-sm hover:text-white transition-colors">Fitur</Link></li>
              <li><Link href="/plans" className="text-sm hover:text-white transition-colors">Pilihan Paket</Link></li>
              <li><Link href="/reviews" className="text-sm hover:text-white transition-colors">Ulasan</Link></li>
              <li><Link href="/history" className="text-sm hover:text-white transition-colors">Riwayat Pesanan</Link></li>
            </ul>
          </div>

          <div className="col-span-1 md:col-span-2 lg:col-span-2">
            <h2 className="text-sm font-semibold text-white mb-5">Bantuan</h2>
            <ul className="space-y-3.5">
              <li><Link href="/help" className="text-sm hover:text-white transition-colors">Pusat Bantuan</Link></li>
              <li><Link href="/blog" className="text-sm hover:text-white transition-colors">Blog</Link></li>
              <li><Link href="/status" className="text-sm hover:text-white transition-colors">Status Layanan</Link></li>
              <li><Link href="/warranty" className="text-sm hover:text-white transition-colors">Ambil Garansi</Link></li>
              <li><Link href="/contact" className="text-sm hover:text-white transition-colors">Hubungi Kami</Link></li>
            </ul>
          </div>

          <div className="col-span-1 md:col-span-2 lg:col-span-2">
            <h2 className="text-sm font-semibold text-white mb-5">Legal</h2>
            <ul className="space-y-3.5">
              <li><Link href="/terms" className="text-sm hover:text-white transition-colors">Syarat & Ketentuan</Link></li>
              <li><Link href="/privacy" className="text-sm hover:text-white transition-colors">Kebijakan Privasi</Link></li>
            </ul>
          </div>
        </div>

        {metodeBayar.length > 0 && (
          <section aria-label="Metode pembayaran" className="border-t border-white/10 mt-12 pt-8 flex flex-col gap-4 md:flex-row md:items-center md:gap-8">
            <h2 className="text-sm font-semibold text-white shrink-0 md:w-40">Metode Pembayaran</h2>
            <ul className="flex flex-wrap gap-2">
              {metodeBayar.map((metode) => (
                <li key={metode.kode} title={metode.nama} className="flex h-9 min-w-[3.5rem] items-center justify-center rounded-md bg-white px-2.5">
                  <img src={metode.logoUrl} alt={metode.nama} loading="lazy" decoding="async" className="h-5 w-auto max-w-[4rem] object-contain" />
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="border-t border-white/10 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-center sm:text-left">
            &copy; {tahunCopyright} {info.appName}. All rights reserved.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-5">
            <IndikatorStatus />
            <p className="text-xs">
              by <span className="font-medium text-white/70">{awalKataKapital(info.ownerName)}</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
